import { notification, Typography } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useEnableAuthenticatorMutation } from '../../../../api/mutations/enableAuthenticator';
import Form from '../../../../components/fields/Form';
import { useThemeComponents } from '../../../../themes';
import useHandleError from '../../../../utilities/useHandleError';
import { ButtonContainer } from './QRCodeStep';

type CodeStepProps = {
    secret: string;
    previous?: () => void;
    next?: () => void;
};

type CodeStepValues = { token: string };

const CodeStep = ({ previous, next, secret }: CodeStepProps) => {
    const { t } = useTranslation('userSelf');
    const {
        Button,
        FormFields: { InputField },
    } = useThemeComponents();

    const [mutation] = useEnableAuthenticatorMutation();

    const onSubmit = useHandleError(
        async ({ token }: CodeStepValues) => {
            // run on the backend
            await mutation({ variables: { token, secret } });

            // success message
            notification.success({
                message: t('userSelf:authenticatorSettings.setup.enabledSuccessMessage.title'),
                description: t('userSelf:authenticatorSettings.setup.enabledSuccessMessage.description'),
            });

            // then move on
            if (next) {
                next();
            }
        },
        [secret, mutation, next, t]
    );

    return (
        <div>
            <Typography.Text style={{ fontSize: '16px' }}>
                {t('userSelf:authenticatorSettings.setup.codeStep.introduction')}
            </Typography.Text>
            <div>
                <Formik<CodeStepValues> initialValues={{ token: '' }} onSubmit={onSubmit}>
                    {({ isSubmitting, handleSubmit }) => (
                        <Form id="codeStepForm" name="codeStepForm" onSubmitCapture={handleSubmit}>
                            <div style={{ width: '100%', padding: '24px 0px 8px' }}>
                                <InputField
                                    {...t('userSelf:authenticatorSettings.fields.code', { returnObjects: true })}
                                    name="token"
                                />
                            </div>
                            <ButtonContainer>
                                <Button disabled={!previous} onClick={previous}>
                                    {t('userSelf:authenticatorSettings.setup.codeStep.cancelButton')}
                                </Button>
                                <Button disabled={isSubmitting} htmlType="submit" type="primary">
                                    {t('userSelf:authenticatorSettings.setup.codeStep.submitButton')}
                                </Button>
                            </ButtonContainer>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default CodeStep;
